import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap, Key, RefreshCw, Check, X, ExternalLink,
  ToggleLeft, ToggleRight, ChevronDown, ChevronUp, Search,
  Eye, EyeOff, Music, Film, Utensils, Heart, DollarSign,
  Plane, Monitor, ShoppingCart, Play, Unlock, AlertCircle,
  Plus, Trash2, PlusCircle, Download, ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  musica:          { label: "Música",           icon: <Music size={14} />,        color: "text-purple-400", bg: "bg-purple-500/10" },
  entretenimiento: { label: "Entretenimiento",   icon: <Film size={14} />,         color: "text-blue-400",   bg: "bg-blue-500/10" },
  comida:          { label: "Comida & Bebidas",  icon: <Utensils size={14} />,     color: "text-orange-400", bg: "bg-orange-500/10" },
  salud:           { label: "Salud",             icon: <Heart size={14} />,        color: "text-red-400",    bg: "bg-red-500/10" },
  finanzas:        { label: "Finanzas",          icon: <DollarSign size={14} />,   color: "text-yellow-400", bg: "bg-yellow-500/10" },
  viajes:          { label: "Viajes & Clima",    icon: <Plane size={14} />,        color: "text-cyan-400",   bg: "bg-cyan-500/10" },
  tecnologia:      { label: "Tecnología",        icon: <Monitor size={14} />,      color: "text-indigo-400", bg: "bg-indigo-500/10" },
  ecommerce:       { label: "E-Commerce",        icon: <ShoppingCart size={14} />, color: "text-teal-400",   bg: "bg-teal-500/10" },
};

const PLAN_COLORS = {
  free:     "bg-green-500/15 text-green-400 border-green-500/30",
  freemium: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  paid:     "bg-red-500/15 text-red-400 border-red-500/30",
};

// ─── Add Custom API Modal ────────────────────────────────────────────────────
const CATEGORY_OPTIONS = [
  { id: "musica", label: "🎵 Música" },
  { id: "entretenimiento", label: "🎬 Entretenimiento" },
  { id: "comida", label: "🍕 Comida & Bebidas" },
  { id: "salud", label: "💊 Salud" },
  { id: "finanzas", label: "💹 Finanzas" },
  { id: "viajes", label: "✈️ Viajes & Clima" },
  { id: "tecnologia", label: "💻 Tecnología" },
  { id: "ecommerce", label: "🛒 E-Commerce" },
  { id: "general", label: "🌐 General" },
];

function AddAPIModal({ onClose, onAdded }) {
  const [form, setForm] = useState({
    name: "", host: "", category: "general", description: "",
    icon: "⚡", plan: "freemium", calls_month: "Según suscripción",
    tags: "", endpoint_url: "", endpoint_method: "GET", query_param: "query",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.host.trim()) {
      toast.error("Nombre y Host son requeridos");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      };
      const r = await axios.post(`${API}/admin/rapidapi/catalog/add`, payload);
      toast.success(r.data.message);
      onAdded();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al añadir API");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-lg animate-scale-in flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-5 pb-4 border-b border-white/10 flex-shrink-0">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <PlusCircle size={18} className="text-yellow-400" />Añadir API de RapidAPI
            </h3>
            <p className="text-gray-500 text-xs mt-0.5">Agrega cualquier API a la que estés suscrito en RapidAPI</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-lg">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="p-3 bg-blue-500/8 border border-blue-500/20 rounded-xl">
            <p className="text-blue-300 text-xs">💡 Ve a <a href="https://rapidapi.com/hub" target="_blank" rel="noopener noreferrer" className="underline">rapidapi.com/hub</a>, suscríbete a la API, copia el host (ej: <code className="text-yellow-300">my-api.p.rapidapi.com</code>) y rellena el formulario.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Nombre de la API *</label>
              <input type="text" value={form.name} onChange={e => set("name", e.target.value)}
                placeholder="ej: My Weather API" className="input-glass w-full" />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Host (RapidAPI) *</label>
              <input type="text" value={form.host} onChange={e => set("host", e.target.value)}
                placeholder="ej: my-api.p.rapidapi.com" className="input-glass w-full font-mono text-xs" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Categoría</label>
              <select value={form.category} onChange={e => set("category", e.target.value)} className="input-glass w-full">
                {CATEGORY_OPTIONS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Ícono (emoji)</label>
              <input type="text" value={form.icon} onChange={e => set("icon", e.target.value)}
                placeholder="⚡" className="input-glass w-full text-center text-lg" maxLength={4} />
            </div>
          </div>

          <div>
            <label className="block text-gray-400 text-xs mb-1.5 font-medium">Descripción</label>
            <input type="text" value={form.description} onChange={e => set("description", e.target.value)}
              placeholder="¿Qué hace esta API?" className="input-glass w-full" />
          </div>

          <div>
            <label className="block text-gray-400 text-xs mb-1.5 font-medium">URL de endpoint de búsqueda</label>
            <input type="text" value={form.endpoint_url} onChange={e => set("endpoint_url", e.target.value)}
              placeholder="ej: https://my-api.p.rapidapi.com/v1/search" className="input-glass w-full font-mono text-xs" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Método HTTP</label>
              <select value={form.endpoint_method} onChange={e => set("endpoint_method", e.target.value)} className="input-glass w-full">
                <option value="GET">GET</option>
                <option value="POST">POST</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Parámetro de query</label>
              <input type="text" value={form.query_param} onChange={e => set("query_param", e.target.value)}
                placeholder="query" className="input-glass w-full font-mono text-xs" />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Llamadas/mes</label>
              <input type="text" value={form.calls_month} onChange={e => set("calls_month", e.target.value)}
                placeholder="Según plan" className="input-glass w-full text-xs" />
            </div>
          </div>

          <div>
            <label className="block text-gray-400 text-xs mb-1.5 font-medium">Tags (separados por coma)</label>
            <input type="text" value={form.tags} onChange={e => set("tags", e.target.value)}
              placeholder="ej: clima, tiempo, temperatura" className="input-glass w-full" />
          </div>
        </div>

        <div className="flex gap-3 p-5 pt-4 border-t border-white/10 flex-shrink-0">
          <button onClick={onClose} className="btn-secondary flex-1">Cancelar</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim() || !form.host.trim()}
            className="btn-primary flex-1 flex items-center justify-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
            Añadir API
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── API Card ─────────────────────────────────────────────────────────────────
function APICard({ api, onToggle, onDelete, connected }) {
  const [expanded, setExpanded] = useState(false);
  const [testing, setTesting] = useState(false);
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const meta = CATEGORY_META[api.category] || {};

  const runTest = async () => {
    if (!query.trim() || testing) return;
    setTesting(true); setResult(null); setTestError(null);
    try {
      const r = await axios.get(`${API}/admin/rapidapi/search/${api.id}`, {
        params: { query: query.trim(), limit: 5 }
      });
      setResult(r.data);
      toast.success(`Respuesta de ${api.name} recibida`);
    } catch (e) {
      setTestError(e.response?.data?.detail || "Error al consultar");
    } finally { setTesting(false); }
  };

  return (
    <div className={`glass-panel overflow-hidden transition-all ${!api.enabled ? "opacity-60" : ""}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-xl ${meta.bg || "bg-white/10"}`}>
              {api.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-white font-semibold text-sm">{api.name}</p>
                {api.subscription_status === "active" && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30 font-medium">
                    ✓ Suscrita
                  </span>
                )}
                {api.subscription_status === "inactive" && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 font-medium">
                    Sin suscripción
                  </span>
                )}
                {api.is_custom && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-medium">
                    Personalizada
                  </span>
                )}
                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${PLAN_COLORS[api.plan] || PLAN_COLORS.freemium}`}>
                  {api.calls_month}
                </span>
              </div>
              <p className="text-gray-500 text-xs mt-0.5 line-clamp-2">{api.description}</p>
              <div className="flex gap-1 mt-1 flex-wrap">
                {(api.tags || []).slice(0, 3).map(t => (
                  <span key={t} className="text-xs px-1.5 py-0.5 bg-white/5 text-gray-500 rounded">{t}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <a href={`https://rapidapi.com/${api.host.replace('.p.rapidapi.com', '').replace('.rapidapi.com', '')}`} target="_blank" rel="noopener noreferrer"
               className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
              <ExternalLink size={14} />
            </a>
            {api.is_custom && onDelete && (
              <button onClick={() => onDelete(api.id, api.name)}
                className="p-1.5 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors">
                <Trash2 size={14} />
              </button>
            )}
            <button onClick={() => onToggle(api.id)} disabled={!connected}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-40">
              {api.enabled
                ? <ToggleRight size={20} className="text-green-400" />
                : <ToggleLeft size={20} className="text-gray-500" />}
            </button>
            <button onClick={() => setExpanded(!expanded)}
              className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors">
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-white/10 p-4 space-y-4">
          <div className="p-3 bg-white/3 rounded-lg text-xs text-gray-400">
            <span className="text-gray-600 mr-2">Host:</span>
            <code className="text-green-400">{api.host}</code>
          </div>

          {connected ? (
            <div className="space-y-3">
              <p className="text-gray-400 text-xs font-medium">Probar API</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && runTest()}
                  placeholder="Escribe algo para probar..."
                  className="flex-1 bg-black/20 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-400"
                />
                <button onClick={runTest} disabled={testing || !query.trim()}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm rounded-xl font-medium transition-colors flex items-center gap-2">
                  {testing ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
                  Probar
                </button>
              </div>

              {testError && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs flex items-start gap-2">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{testError}</span>
                </div>
              )}

              {result && (
                <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                  <p className="text-green-400 text-xs font-medium mb-2 flex items-center gap-1">
                    <Check size={12} />Respuesta de {result.api_name} — HTTP {result.status}
                  </p>
                  <pre className="text-gray-400 text-xs overflow-auto max-h-48 whitespace-pre-wrap">
                    {JSON.stringify(result.raw, null, 2).slice(0, 1200)}{JSON.stringify(result.raw).length > 1200 ? "\n..." : ""}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg text-yellow-400 text-xs">
              Conecta tu clave RapidAPI para probar esta API
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Import Hosts Modal ──────────────────────────────────────────────────────
function ImportHostsModal({ onClose, onImported }) {
  const [hostsText, setHostsText] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    const lines = hostsText
      .split(/[\n,;]+/)
      .map(l => l.trim())
      .filter(Boolean);
    if (!lines.length) { toast.error("Ingresa al menos un host"); return; }
    setImporting(true);
    try {
      const r = await axios.post(`${API}/admin/rapidapi/import-hosts`, { hosts: lines }, { timeout: 120000 });
      setResult(r.data);
      if (r.data.imported > 0) {
        toast.success(`✅ ${r.data.imported} APIs importadas correctamente`);
        onImported();
      } else {
        toast.info("Todas las APIs ya estaban en el catálogo");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al importar");
    } finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-xl animate-scale-in flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-5 pb-4 border-b border-white/10 flex-shrink-0">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Download size={18} className="text-green-400" />Importar suscripciones de RapidAPI
            </h3>
            <p className="text-gray-500 text-xs mt-0.5">Sincroniza tus APIs suscritas pegando sus hosts aquí</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Steps */}
          <div className="bg-white/3 border border-white/8 rounded-xl p-4 space-y-3">
            <p className="text-white text-sm font-medium">Cómo obtener tus hosts de RapidAPI:</p>
            <ol className="space-y-2">
              {[
                <>Ve a <a href="https://rapidapi.com/developer/apps" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">rapidapi.com/developer/apps</a> → <strong>My Subscriptions</strong></>,
                <>Haz clic en cada API suscrita y copia el host que aparece en los ejemplos de código (ej: <code className="text-green-400 bg-white/5 px-1 rounded">tasty.p.rapidapi.com</code>)</>,
                <>Pega todos los hosts abajo, uno por línea. OkBot los agregará automáticamente a tu catálogo.</>,
              ].map((step, i) => (
                <li key={i} className="flex gap-2.5 text-xs text-gray-400">
                  <span className="w-5 h-5 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center flex-shrink-0 font-bold text-[10px]">{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Tip box */}
          <div className="bg-blue-500/8 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-300">
            <strong>Tip:</strong> También puedes pegar el nombre de la API y OkBot intentará encontrar el host correcto. 
            El formato válido es <code className="bg-white/5 px-1 rounded">nombre.p.rapidapi.com</code> — si solo pones el nombre, se añade el sufijo automáticamente.
          </div>

          {/* Textarea */}
          <div>
            <label className="text-gray-400 text-xs font-medium block mb-2">Hosts de tus APIs suscritas (uno por línea):</label>
            <textarea
              value={hostsText}
              onChange={e => setHostsText(e.target.value)}
              placeholder={"tasty.p.rapidapi.com\ntripgo.p.rapidapi.com\nduckduckgo8.p.rapidapi.com\nspotify-scraper.p.rapidapi.com\n..."}
              rows={8}
              className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-gray-200 text-xs placeholder-gray-600 focus:outline-none focus:border-white/20 resize-none font-mono"
            />
            <p className="text-gray-600 text-xs mt-1">{hostsText.split(/[\n,;]+/).filter(l => l.trim()).length} hosts ingresados</p>
          </div>

          {/* Result */}
          {result && (
            <div className="bg-green-500/8 border border-green-500/20 rounded-xl p-4 text-xs">
              <p className="text-green-400 font-medium mb-2">Resultado de la importación:</p>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="text-center"><p className="text-green-400 text-lg font-bold">{result.imported}</p><p className="text-gray-500">Importadas</p></div>
                <div className="text-center"><p className="text-gray-400 text-lg font-bold">{result.skipped}</p><p className="text-gray-500">Ya existían</p></div>
                <div className="text-center"><p className="text-red-400 text-lg font-bold">{result.errors}</p><p className="text-gray-500">Errores</p></div>
              </div>
              {result.apis?.length > 0 && (
                <div className="space-y-1 mt-2">
                  {result.apis.map(a => (
                    <div key={a.id} className="flex items-center gap-2">
                      <Check size={11} className="text-green-400" />
                      <span className="text-gray-300">{a.name}</span>
                      <span className="text-gray-600">({a.host})</span>
                      {a.subscription_status === "active" && <span className="text-green-400 text-[10px] bg-green-500/15 px-1.5 rounded-full">✓ Activa</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-5 pt-4 border-t border-white/10 flex-shrink-0 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-white/10 text-gray-400 rounded-xl hover:bg-white/5 text-sm">
            {result ? "Cerrar" : "Cancelar"}
          </button>
          {!result && (
            <button onClick={handleImport} disabled={importing || !hostsText.trim()}
              className="flex-1 py-2.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-400 rounded-xl text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
              {importing ? <><RefreshCw size={14} className="animate-spin" />Importando...</> : <><Download size={14} />Importar APIs</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RapidAPIManager() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/rapidapi/status`);
      setStatus(r.data);
    } catch (e) {
      toast.error("Error al cargar estado de RapidAPI");
    } finally { setLoading(false); }
  }, []);

  const handleAutoDetect = async () => {
    if (!connected) { toast.error("Configura tu RapidAPI Key primero"); return; }
    setAutoDetecting(true);
    try {
      const r = await axios.post(`${API}/admin/rapidapi/auto-detect`, {}, { timeout: 180000 });
      const d = r.data;
      toast.success(`✅ Auto-detección: ${d.imported} APIs nuevas, ${d.verified_active} activas de ${d.total_apis}`, { duration: 6000 });
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en auto-detección");
    } finally { setAutoDetecting(false); }
  };

  const handleRefresh = async () => {
    if (!status?.key_configured) {
      toast.error("Primero configura tu RapidAPI Key");
      return;
    }
    setRefreshing(true);
    const toastId = toast.loading(`Verificando ${status?.total_apis || 0} APIs del catálogo… (puede tardar ~20s)`);
    try {
      const r = await axios.post(`${API}/admin/rapidapi/refresh`, {}, { timeout: 90000 });
      const d = r.data;
      toast.dismiss(toastId);
      const parts = [];
      if (d.new_apis_added > 0) parts.push(`+${d.new_apis_added} nuevas en catálogo`);
      if (d.verified_active >= 0) parts.push(`${d.verified_active} de ${d.total_apis} con suscripción activa`);
      const msg = parts.length ? parts.join(" · ") : "Catálogo verificado";
      toast.success(`✅ ${msg}`);
      await loadStatus();
    } catch (e) {
      toast.dismiss(toastId);
      const detail = e?.response?.data?.detail || "Error al actualizar el catálogo";
      toast.error(detail);
    } finally { setRefreshing(false); }
  };

  const handleDelete = async (apiId, apiName) => {
    if (!window.confirm(`¿Eliminar "${apiName}" del catálogo personalizado?`)) return;
    try {
      await axios.delete(`${API}/admin/rapidapi/catalog/${apiId}`);
      toast.success(`API "${apiName}" eliminada`);
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar API");
    }
  };

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const saveKey = async () => {
    if (!apiKey.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/rapidapi/configure`, { api_key: apiKey.trim() });
      toast.success("Clave RapidAPI guardada");
      setApiKey("");
      await loadStatus();
    } catch (e) {
      toast.error("Error al guardar la clave");
    } finally { setSaving(false); }
  };

  const handleToggle = async (apiId) => {
    try {
      await axios.put(`${API}/admin/rapidapi/${apiId}/toggle`);
      await loadStatus();
    } catch { toast.error("Error al cambiar estado"); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <RefreshCw size={24} className="text-red-400 animate-spin" />
    </div>
  );

  const apis = status?.apis || [];
  const connected = status?.connected;

  const filtered = apis.filter(a => {
    const matchCat = activeCategory === "all" || a.category === activeCategory;
    const matchSearch = !searchFilter ||
      a.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      a.description.toLowerCase().includes(searchFilter.toLowerCase()) ||
      a.tags?.some(t => t.toLowerCase().includes(searchFilter.toLowerCase()));
    return matchCat && matchSearch;
  });

  const categories = [...new Set(apis.map(a => a.category))];

  const grouped = categories.reduce((acc, cat) => {
    const items = filtered.filter(a => a.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  const lastRefreshedDisplay = (() => {
    const ts = status?.last_refreshed;
    if (!ts) return null;
    const d = typeof ts === "number" ? new Date(ts * 1000) : new Date(ts);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  })();

  return (
    <div>
      {showAddModal && (
        <AddAPIModal onClose={() => setShowAddModal(false)} onAdded={loadStatus} />
      )}
      {showImportModal && (
        <ImportHostsModal onClose={() => setShowImportModal(false)} onImported={loadStatus} />
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-white font-semibold text-lg flex items-center gap-2">
            <Zap size={20} className="text-yellow-400" />RapidAPI Hub
          </h3>
          <p className="text-gray-500 text-sm mt-0.5">
            Una sola clave da acceso a miles de APIs. Activa las que quieras y el agente OkBot las usará automáticamente.
          </p>
          {lastRefreshedDisplay && (
            <p className="text-gray-600 text-xs mt-1">
              Última actualización del catálogo: {lastRefreshedDisplay}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={loadStatus} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg" title="Recargar estado">
            <RefreshCw size={16} />
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            title="Verifica qué APIs del catálogo tienen suscripción activa con tu clave"
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500/15 hover:bg-yellow-500/25 border border-yellow-500/30 text-yellow-400 text-sm rounded-xl font-medium transition-colors disabled:opacity-50"
          >
            {refreshing ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
            {refreshing ? "Verificando..." : "Actualizar"}
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            disabled={!connected}
            title="Importa tus suscripciones de RapidAPI pegando sus hosts"
            className="flex items-center gap-2 px-3 py-2 bg-green-500/15 hover:bg-green-500/25 border border-green-500/30 text-green-400 text-sm rounded-xl font-medium transition-colors disabled:opacity-40"
          >
            <Download size={14} />
            Importar suscripciones
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/30 text-indigo-300 text-sm rounded-xl font-medium transition-colors"
          >
            <Plus size={14} />
            Añadir API
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="glass-panel p-4 text-center">
          <p className={`text-2xl font-bold ${connected ? "text-green-400" : "text-red-400"}`}>
            {connected ? "●" : "○"}
          </p>
          <p className="text-gray-500 text-xs mt-0.5">{connected ? "Conectado" : "Sin conectar"}</p>
        </div>
        <div className="glass-panel p-4 text-center">
          <p className="text-2xl font-bold text-white">{status?.total_apis || 0}</p>
          <p className="text-gray-500 text-xs mt-0.5">APIs en catálogo</p>
        </div>
        <div className="glass-panel p-4 text-center">
          <p className="text-2xl font-bold text-green-400">{status?.verified_active || 0}</p>
          <p className="text-gray-500 text-xs mt-0.5">Suscritas activas</p>
        </div>
        <div className="glass-panel p-4 text-center">
          <p className="text-2xl font-bold text-blue-400">{status?.enabled_apis || 0}</p>
          <p className="text-gray-500 text-xs mt-0.5">Habilitadas OkBot</p>
        </div>
      </div>

      {/* Auto-detect prominent banner */}
      {connected && (
        <div className="p-4 bg-gradient-to-r from-purple-500/15 to-pink-500/10 border border-purple-500/30 rounded-2xl mb-4 flex gap-4 items-center">
          <div className="w-12 h-12 rounded-xl bg-purple-500/25 flex items-center justify-center flex-shrink-0">
            <Zap size={24} className="text-purple-300" />
          </div>
          <div className="flex-1">
            <p className="text-purple-100 text-sm font-semibold">⚡ Auto-detectar suscripciones de RapidAPI</p>
            <p className="text-purple-200/70 text-xs mt-0.5">
              Importa todas las APIs populares + verifica con tu key cuáles tienes activas. Un solo click.
              Se ejecuta también cada 6 horas en background para detectar nuevas suscripciones.
            </p>
          </div>
          <button onClick={handleAutoDetect} disabled={autoDetecting}
            className="flex items-center gap-2 px-4 py-2.5 bg-purple-500 hover:bg-purple-600 disabled:bg-purple-500/40 text-white text-sm rounded-xl font-semibold flex-shrink-0 transition-colors shadow-lg shadow-purple-500/20">
            {autoDetecting ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
            {autoDetecting ? "Detectando…" : "Auto-detectar ahora"}
          </button>
        </div>
      )}

      {/* Info banner: importar suscripciones */}
      {connected && (
        <div className="p-3.5 bg-green-500/8 border border-green-500/20 rounded-xl mb-5 flex gap-3 items-start">
          <Download size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-300 text-xs font-medium">¿Tienes una API que no aparece tras el auto-detect?</p>
            <p className="text-green-500/80 text-xs mt-0.5">
              Pégale el host (ej: <code className="text-green-300">mi-api.p.rapidapi.com</code>) en
              <strong className="text-green-400"> Importar suscripciones</strong> y OkBot la agrega + verifica.
            </p>
          </div>
          <button onClick={() => setShowImportModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-400 text-xs rounded-lg flex-shrink-0 font-medium transition-colors">
            <ArrowRight size={12} />Importar host
          </button>
        </div>
      )}

      {/* API Key config */}
      <div className="glass-panel p-5 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${connected ? "bg-green-500/20" : "bg-yellow-500/20"}`}>
            <Key size={18} className={connected ? "text-green-400" : "text-yellow-400"} />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">
              {connected ? "RapidAPI Conectado" : "Conectar RapidAPI"}
            </p>
            <p className="text-gray-500 text-xs">
              {connected
                ? "Tu clave está activa. Usa 'Actualizar' para verificar qué suscripciones están activas."
                : "Ingresa tu clave maestra de RapidAPI para activar todas las integraciones."}
            </p>
          </div>
          {connected && <Check size={18} className="text-green-400 ml-auto" />}
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type={showKey ? "text" : "password"}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              onKeyDown={e => e.key === "Enter" && saveKey()}
              placeholder={connected ? "Ingresa nueva clave para actualizar..." : "Pega tu RapidAPI Key aquí..."}
              className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-400 pr-10"
            />
            <button onClick={() => setShowKey(!showKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
              {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <button onClick={saveKey} disabled={!apiKey.trim() || saving}
            className="px-4 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm rounded-xl font-medium transition-colors flex items-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            {connected ? "Actualizar" : "Conectar"}
          </button>
        </div>

        {!connected && (
          <div className="mt-3 p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
            <p className="text-blue-400 text-xs font-medium mb-1">¿Cómo obtener tu clave?</p>
            <ol className="text-gray-500 text-xs space-y-0.5 list-decimal list-inside">
              <li>Ve a <a href="https://rapidapi.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">rapidapi.com</a> e inicia sesión</li>
              <li>Haz clic en tu perfil → "My Apps" → selecciona o crea una app</li>
              <li>Copia la clave que aparece en "Application Key"</li>
              <li>Pégala aquí — con esta clave accedes a todas las APIs que suscribas</li>
            </ol>
          </div>
        )}
      </div>

      {/* Search + filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input type="text" value={searchFilter} onChange={e => setSearchFilter(e.target.value)}
            placeholder="Buscar APIs..."
            className="input-glass w-full pl-9 text-sm" />
        </div>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setActiveCategory("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeCategory === "all" ? "bg-red-500 text-white" : "bg-white/5 text-gray-400 hover:bg-white/10"}`}>
            Todas
          </button>
          {categories.map(cat => {
            const meta = CATEGORY_META[cat];
            return (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                  activeCategory === cat ? "bg-red-500 text-white" : `${meta?.bg || "bg-white/5"} ${meta?.color || "text-gray-400"} hover:opacity-80`
                }`}>
                {meta?.icon}{meta?.label || cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* API list grouped */}
      <div className="space-y-8">
        {Object.entries(grouped).map(([cat, catApis]) => {
          const meta = CATEGORY_META[cat];
          return (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className={meta?.color || "text-gray-400"}>{meta?.icon}</span>
                <h4 className={`font-semibold text-sm ${meta?.color || "text-gray-300"}`}>{meta?.label || cat}</h4>
                <span className="text-gray-600 text-xs">({catApis.length})</span>
              </div>
              <div className="space-y-3">
                {catApis.map(api => (
                  <APICard key={api.id} api={api} onToggle={handleToggle} onDelete={handleDelete} connected={connected} />
                ))}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="glass-panel p-12 text-center">
            <Zap size={32} className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No se encontraron APIs</p>
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
        <p className="text-yellow-400 text-sm font-medium mb-1">💡 ¿Cómo funciona con OkBot?</p>
        <p className="text-gray-500 text-xs">
          Con una sola clave RapidAPI, OkBot puede consultar automáticamente cualquier API activa para enriquecer las respuestas del agente.
          Suscríbete a las APIs que necesites en{" "}
          <a href="https://rapidapi.com/hub" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:underline">rapidapi.com/hub</a>{" "}
          (muchas son gratuitas) y quedarán disponibles inmediatamente.
        </p>
      </div>
    </div>
  );
}
